import React, { HTMLProps, ReactNode, forwardRef, useEffect, useRef } from 'react'

import { Button } from '../../../common/components/Button'
import Chevron from '../../../common/components/Chevron'
import { DropdownMenu } from '../../../common/components/Menu'
import { ScreenDeviceType } from '../../../common/enums/screenDeviceTypeEnum'
import useMediaQueryHook from '../../../common/hooks/useMediaQueryHook'
import { useIsComponentVisible } from '../../../common/hooks/useIsComponentVisible'
import { CloseIcon } from '../../../common/icons/close-icon/CloseIcon'
import FilterIcon from '../../../lib/icons/filter-icon/filter-icon'
import SearchIcon from '../../../lib/icons/search-icon/search-icon'
import { theme } from '../../../lib/theme'
import { KeyEvents } from '../../../common/constants/KeyEventContants'
import useText from '../../../common/hooks/useText'
import { DecoratedFilterValue } from '../../types/DecoratedFilterValue'
import { FilterValue } from '../../types/FilterValue'

import { ButtonContainer, StyledInput } from './search-input.styles'

export type SearchInputProps = {
    onSubmit: () => void
    onClear: (filterName?: string) => void
    filterDropdownItems?: ReactNode
    activeFilters?: { key: string; value: string | number | boolean; label?: string; labelValue?: string }[]
} & HTMLProps<HTMLInputElement>

type FilterDropdownProps = {
    isOpen: boolean
    close: () => void
} & HTMLProps<HTMLElement>

const FilterDropdown = forwardRef<HTMLDivElement, FilterDropdownProps>(({ children, isOpen }, ref) => {
    return (
        isOpen && (
            <DropdownMenu
                ref={ref}
                className="absolute right-0 xs:left-0 z-1 flex flex-column gap-4 z-2 text-sm w-full md:w-30rem"
                style={{ top: '50px' }}
                data-testid="search-input-filter-dropdown"
                tabIndex={-1}
            >
                {children}
            </DropdownMenu>
        )
    )
})

type ActiveFilterButtonProps = {
    label: string
    clearFilter: () => void
}

const ActiveFilterButton = ({ label, clearFilter }: ActiveFilterButtonProps) => {
    return (
        <Button
            label={label}
            onClick={() => clearFilter()}
            icon={<FilterIcon size={16} fill={theme.font.textColor} />}
            className="ghost p-4 pr-5 text-500 ml-auto"
        >
            <CloseIcon size={16} className="ml-2 justify-self-end" />
        </Button>
    )
}

type ActiveFilterChipsProps = {
    activeFilters: DecoratedFilterValue[]
    clearFilter: (filterName: string) => void
} & HTMLProps<HTMLDivElement>

const ActiveFilterChips = ({ activeFilters, clearFilter }: ActiveFilterChipsProps) => {
    const text = useText()
    return (
        <div className="flex align-items-start py-3 pl-4 gap-2">
            <div className="flex gap-2 flex-nowrap min-w-max text-500 mr-4" style={{ marginTop: '3.5px' }}>
                <FilterIcon size={16} />
                <div>{text('lblActiveFiltersHeading')}</div>
            </div>
            <div className="flex flex-wrap justify-content-end align-items-center gap-2">
                {activeFilters.map((filter) => (
                    <Button
                        key={filter.key}
                        className="border-300 surface-100 text-500"
                        size="xxs"
                        icon={<CloseIcon size={14} />}
                        iconPosition="right"
                        onClick={() => clearFilter(filter.key)}
                        data-testid={`active-filter-${filter.key}`}
                    >
                        <span className="capitalize">{filter.label ?? filter.key}</span>
                        {filter.labelValue && ':'}
                        <span className="text-gray-800 font-medium">{filter.labelValue ?? filter.value}</span>
                    </Button>
                ))}
            </div>
        </div>
    )
}

type ActiveFilterContainerProps = {
    activeFilters: FilterValue[]
    clear: (filterName?: string) => void
}

const ActiveFilterContainer = ({ activeFilters, clear }: ActiveFilterContainerProps) => {
    const text = useText()
    const deviceType = useMediaQueryHook()

    return deviceType === ScreenDeviceType.Mobile ? (
        <ActiveFilterButton
            label={text('lblActiveFilters', { activeFilters: activeFilters.length })}
            clearFilter={clear}
        />
    ) : (
        <ActiveFilterChips activeFilters={activeFilters} clearFilter={clear} />
    )
}

const SearchWithFilter = forwardRef<HTMLInputElement, SearchInputProps>(
    ({ onSubmit, onClear, activeFilters, filterDropdownItems, ...rest }, ref): React.ReactElement => {
        const text = useText()
        const buttonContainerRef = useRef<HTMLDivElement>(null)
        const { isComponentVisible, setIsComponentVisible } = useIsComponentVisible(false, buttonContainerRef)
        useEffect(() => {
            const keyDownListener = (event: KeyboardEvent) => {
                if (event.key === KeyEvents.ESCAPE) {
                    setIsComponentVisible(false)
                }
            }
            const keypressListener = (event: KeyboardEvent) => {
                if (event.key === KeyEvents.ENTER) {
                    onSubmit()
                }
            }

            window.addEventListener('keydown', keyDownListener)
            window.addEventListener('keypress', keypressListener)
            return () => {
                window.removeEventListener('keydown', keyDownListener)
                window.removeEventListener('keypress', keypressListener)
            }
        }, [onSubmit, setIsComponentVisible])

        const toggleFilterMenu = (shouldOpenFilterMenu: boolean) => {
            if (!isComponentVisible && activeFilters && activeFilters.length === 0) {
                onClear()
            }
            setIsComponentVisible(shouldOpenFilterMenu)
        }

        return (
            <form className="w-full relative" onSubmit={onSubmit}>
                <div className="gap-4 sm:gap-0 flex flex-row flex-wrap sm:flex-nowrap">
                    <StyledInput
                        ref={ref}
                        icon={<SearchIcon fill={theme.font.textColor} size={20} />}
                        type="search"
                        wrapperClassName="w-12 md:w-12 xl:w-10 rounded m-0"
                        className="text-lg max-h-2rem"
                        {...rest}
                    />
                    <ButtonContainer className="flex w-full sm:w-10rem" ref={buttonContainerRef}>
                        <Button
                            className="border-noround-right text-base flex-1"
                            label={text('btnSearch')}
                            onClick={() => {
                                onSubmit()
                                toggleFilterMenu(false)
                            }}
                        />
                        {filterDropdownItems && (
                            <Button
                                data-testid="filter-dropdown-btn"
                                icon={
                                    <Chevron
                                        flipped={!isComponentVisible}
                                        color={
                                            activeFilters && activeFilters.length > 0
                                                ? theme.brandColors.CA_Green
                                                : theme.brandColors.CA_White
                                        }
                                        size={20}
                                    />
                                }
                                className="border-noround-left w-1rem filter-dropdown-btn"
                                onClick={() => {
                                    setIsComponentVisible(!isComponentVisible)
                                }}
                            />
                        )}
                    </ButtonContainer>
                </div>
                <FilterDropdown isOpen={isComponentVisible} close={() => setIsComponentVisible(false)}>
                    {filterDropdownItems}
                    <div className="flex flex-column gap-2">
                        <Button
                            label={text('btnClearFilter')}
                            className="w-full secondary"
                            onClick={() => {
                                onClear()
                                toggleFilterMenu(false)
                            }}
                        />
                        <Button
                            label={text('btnApply')}
                            className="w-full"
                            onClick={() => {
                                onSubmit()
                                toggleFilterMenu(false)
                            }}
                        />
                    </div>
                </FilterDropdown>

                {activeFilters && activeFilters.length > 0 && (
                    <ActiveFilterContainer
                        activeFilters={activeFilters}
                        clear={(filterName?: string) => {
                            onClear(filterName)
                            toggleFilterMenu(false)
                        }}
                    />
                )}
            </form>
        )
    }
)

export default SearchWithFilter
